import React, { useState, useMemo } from 'react';
import { useVisualizationStore } from '../store/visualizationStore';
import { VisualizationLink, getLinkEndpointId } from '../types/infrastructure';

export const ControlsPanel: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [useWebGL, setUseWebGL] = useState(false);

  const {
    showNodeTypes,
    showConnectionTypes,
    autoLayoutType,
    highlightFixedNodes,
    isSimulationRunning,
    setFilters,
    setAutoLayoutType,
    setHighlightFixedNodes,
    fixAllVisibleNodes,
    releaseAllVisibleNodes,
    currentContext,
    nodes,
    links,
    reset
  } = useVisualizationStore();

  const nodeTypes = ['site', 'pod', 'network', 'rack', 'device', 'component'];

  // Get available connection types using the exact same logic as VisualizationEngine
  const connectionTypes = useMemo(() => {
    if (!links || links.length === 0 || !nodes || nodes.length === 0) return [];
    
    // Replicate VisualizationEngine.getVisibleElements() logic exactly
    let visibleNodes = nodes.filter(node => showNodeTypes.includes(node.type));
    
    // Apply hierarchical context filtering
    if (currentContext) {
      const contextNode = nodes.find(n => n.id === currentContext);
      const isNetworkContext = contextNode?.type === 'network';

      visibleNodes = visibleNodes.filter(node => {
        if (node.parent === currentContext) {
          return true;
        }
        if (isNetworkContext && node.type === 'device') {
          const deviceRackParent = node.parent;
          const rackNode = nodes.find(n => n.id === deviceRackParent);
          if (rackNode && rackNode.parent === currentContext) {
            return true;
          }
        }
        return false;
      });
    } else {
      visibleNodes = visibleNodes.filter(node => {
        return node.type === 'site' || !node.parent;
      });
    }

    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));

    // Smart link filtering with aggregation (same as VisualizationEngine)
    const visibleLinks: VisualizationLink[] = [];
    const connectionCounts = new Map<string, {link: VisualizationLink, count: number, totalBandwidth: number}>();

    for (const link of links) {
      let sourceNode = getLinkEndpointId(link.source);
      let targetNode = getLinkEndpointId(link.target);

      // If the link endpoints are not visible, try to find their visible ancestors
      if (!visibleNodeIds.has(sourceNode)) {
        const sourceComponent = nodes.find(n => n.id === sourceNode);
        if (sourceComponent && sourceComponent.parent && visibleNodeIds.has(sourceComponent.parent)) {
          sourceNode = sourceComponent.parent;
        } else {
          continue;
        }
      }

      if (!visibleNodeIds.has(targetNode)) {
        const targetComponent = nodes.find(n => n.id === targetNode);
        if (targetComponent && targetComponent.parent && visibleNodeIds.has(targetComponent.parent)) {
          targetNode = targetComponent.parent;
        } else {
          continue;
        }
      }

      // Skip internal connections (where source and target are the same device after aggregation)
      if (sourceNode === targetNode) {
        continue; // This is an internal connection, skip it for network-level view
      }

      // Aggregate connections between the same nodes
      const connectionKey = `${sourceNode}-${targetNode}`;
      const reverseKey = `${targetNode}-${sourceNode}`;
      const useKey = connectionCounts.has(connectionKey) ? connectionKey :
                     connectionCounts.has(reverseKey) ? reverseKey : connectionKey;

      const linkBandwidth = link.bandwidth?.value || 0;

      if (connectionCounts.has(useKey)) {
        const existing = connectionCounts.get(useKey)!;
        existing.totalBandwidth += linkBandwidth;
      } else {
        connectionCounts.set(useKey, {
          link: {
            ...link,
            source: sourceNode,
            target: targetNode,
            id: `aggregated-${connectionCounts.size}`
          },
          count: 1,
          totalBandwidth: linkBandwidth
        });
      }
    }

    // Convert aggregated connections to visible links
    for (const {link} of Array.from(connectionCounts.values())) {
      visibleLinks.push(link);
    }

    // Extract unique protocols from visible aggregated links
    const types = new Set<string>();
    visibleLinks.forEach(link => {
      if (link.protocol) {
        types.add(link.protocol);
      }
    });
    
    return Array.from(types).sort();
  }, [links, nodes, showNodeTypes, currentContext]);


  const handleNodeTypeToggle = (nodeType: string) => {
    const newTypes = showNodeTypes.includes(nodeType)
      ? showNodeTypes.filter(t => t !== nodeType)
      : [...showNodeTypes, nodeType];
    setFilters(newTypes, showConnectionTypes);
  };

  const handleConnectionTypeToggle = (connectionType: string) => {
    const newTypes = showConnectionTypes.includes(connectionType)
      ? showConnectionTypes.filter(t => t !== connectionType)
      : [...showConnectionTypes, connectionType];
    setFilters(showNodeTypes, newTypes);
  };

  const handleSelectAllNodes = () => {
    setFilters(nodeTypes, showConnectionTypes);
  };

  const handleDeselectAllNodes = () => {
    setFilters([], showConnectionTypes);
  };

  const handleSelectAllConnections = () => {
    setFilters(showNodeTypes, connectionTypes);
  };

  const handleDeselectAllConnections = () => {
    setFilters(showNodeTypes, []);
  };

  const getNodeTypeIcon = (type: string) => {
    const icons = {
      site: '🌍',
      pod: '🏢',
      network: '🔗',
      rack: '📦',
      device: '💾',
      component: '🔧'
    };
    return icons[type as keyof typeof icons] || '📍';
  };

  const getNodeTypeColor = (type: string) => {
    const colors = {
      site: 'bg-blue-100 text-blue-800',
      pod: 'bg-orange-100 text-orange-800',
      network: 'bg-cyan-100 text-cyan-800',
      rack: 'bg-green-100 text-green-800',
      device: 'bg-red-100 text-red-800',
      component: 'bg-purple-100 text-purple-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getConnectionTypeIcon = (type: string) => {
    const icons = {
      'InfiniBand': '⚡',
      'Ethernet': '🌐',
      'NVLink': '🔗',
      'PCIe': '💾',
      'Custom': '⚙️',
      'Unknown': '❓'
    };
    return icons[type as keyof typeof icons] || '🔗';
  };

  const getConnectionTypeColor = (type: string) => {
    const colors = {
      'Ethernet': 'bg-gray-100 text-gray-700',        // Matches #999 (lighter gray)
      'InfiniBand': 'bg-gray-200 text-gray-800',      // Matches #777 (darker gray)  
      'NVLink': 'bg-green-100 text-green-700',        // Matches #76b900 (NVIDIA green)
      'PCIe': 'bg-teal-100 text-teal-700',            // Matches #0891b2 (teal)
      'Custom': 'bg-yellow-100 text-yellow-700',      // Matches #eab308 (yellow)
      'Unknown': 'bg-gray-100 text-gray-600'          // Matches #6b7280 (medium gray)
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="controls-panel absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-white shadow-lg rounded-lg px-4 py-2 hover:shadow-xl transition-shadow mb-2 w-full"
      >
        <div className="flex items-center justify-center space-x-2">
          <span>⚙️</span>
          <span className="font-medium">Controls</span>
          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
      </button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="bg-white shadow-lg rounded-lg p-4 w-96 max-h-96 overflow-y-auto">
          {/* Rendering Options */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold mb-2 text-gray-700">Rendering</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="webgl"
                  checked={useWebGL}
                  onChange={(e) => setUseWebGL(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="webgl" className="text-sm text-gray-700">
                  Use WebGL (Better performance for large datasets)
                </label>
              </div>

              {/* Manual Layout Actions */}
              <div className="border-t pt-2 mt-2">
                <label className="text-sm text-gray-700 mb-2 block font-medium">
                  Node Position Control
                </label>
                <div className="space-y-2">
                  <button
                    onClick={fixAllVisibleNodes}
                    disabled={isSimulationRunning}
                    className="w-full px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    📌 Fix All Visible Nodes
                  </button>
                  <button
                    onClick={releaseAllVisibleNodes}
                    className="w-full px-3 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors text-sm"
                  >
                    🔓 Release All Visible Nodes
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {isSimulationRunning ?
                    'Waiting for layout simulation to finish...' :
                    'Fix nodes to save their current positions. Release nodes to remove fixed coordinates.'
                  }
                </div>
              </div>

              {/* Auto-Layout Type Selection */}
              <div className="border-t pt-2 mt-2">
                <label htmlFor="autolayout-type" className="text-sm text-gray-700 mb-1 block font-medium">
                  Auto-Layout Topology Type
                </label>
                <select
                  id="autolayout-type"
                  value={autoLayoutType}
                  onChange={(e) => setAutoLayoutType(e.target.value as 'auto' | 'hierarchical' | 'ring' | 'dragonfly')}
                  className="text-sm border rounded px-2 py-1 w-full"
                >
                  <option value="auto">Auto-detect</option>
                  <option value="hierarchical">Force Hierarchical (Spine-Leaf)</option>
                  <option value="ring">Force Ring/Circular</option>
                  <option value="dragonfly">Force Dragonfly</option>
                </select>
                <div className="text-xs text-gray-500 mt-1">
                  Controls topology detection for nodes without fixed positions. Auto-detect analyzes connectivity patterns.
                </div>
              </div>

              <div className="flex items-center space-x-2 border-t pt-2 mt-2">
                <input
                  type="checkbox"
                  id="highlight-fixed"
                  checked={highlightFixedNodes}
                  onChange={(e) => setHighlightFixedNodes(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="highlight-fixed" className="text-sm text-gray-700">
                  Highlight nodes with fixed positions
                </label>
              </div>
            </div>
          </div>

          {/* Node Type Filters */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Node Types</h3>
              <div className="space-x-1">
                <button
                  onClick={handleSelectAllNodes}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  All
                </button>
                <button
                  onClick={handleDeselectAllNodes}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  None
                </button>
              </div>
            </div>
            <div className="space-y-1">
              {nodeTypes.map(type => (
                <label key={type} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showNodeTypes.includes(type)}
                    onChange={() => handleNodeTypeToggle(type)}
                    className="rounded"
                  />
                  <span className={`px-2 py-1 rounded text-xs ${getNodeTypeColor(type)}`}>
                    {getNodeTypeIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Connection Type Filters */}
          {connectionTypes.length > 0 && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Connection Types</h3>
                <div className="space-x-1">
                  <button
                    onClick={handleSelectAllConnections}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    All
                  </button>
                  <button
                    onClick={handleDeselectAllConnections}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {connectionTypes.map(type => (
                  <label key={type} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showConnectionTypes.includes(type)}
                      onChange={() => handleConnectionTypeToggle(type)}
                      className="rounded"
                    />
                    <span className={`px-2 py-1 rounded text-xs ${getConnectionTypeColor(type)}`}>
                      {getConnectionTypeIcon(type)} {type}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold mb-2 text-gray-700">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={reset}
                className="w-full px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm"
              >
                Reset Visualization
              </button>
            </div>
          </div>

          {/* Performance Info */}
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            <div className="font-medium mb-1">Performance Tips:</div>
            <ul className="space-y-1">
              <li>• Enable WebGL for large datasets (10K+ nodes)</li>
              <li>• Hide unused node types to improve performance</li>
              <li>• Use appropriate zoom level for your use case</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};